import { matchesAllPrefixes, messageSearchText, resolveEditTarget } from "./search_text";

// The chat's one door to a message search backend. V1 asks the homeserver
// (tuwunel's POST /search): the whole history, membership checked by the
// server — but it matches whole words only. A backend that matches word
// prefixes replaces the body of searchMessages and flips capabilities.prefix;
// callers only ever see hits of this shape:
//
//   { eventId, roomId, sender, ts, msgtype, text }
//
// matrix-js-sdk's searchRoomEvents is not used: it always asks for context
// events and profiles and takes no limit, none of which the lists need.

// tuwunel's cap on filter.limit.
const MAX_LIMIT = 100;

export function createSearchEngine(client) {
  return {
    capabilities: { prefix: false },

    async searchMessages({ term, tokens = [], roomIds = null, cursor = null, limit = 20, signal } = {}) {
      // Always name the rooms: left out, the server searches every room it
      // knows the user in, including ones this client does not show.
      const rooms = roomIds || joinedRoomIds(client);
      if (!term || rooms.length === 0) return { hits: [], cursor: null, total: 0 };

      const body = {
        search_categories: {
          room_events: {
            search_term: term,
            filter: { rooms, limit: Math.min(limit, MAX_LIMIT) }
          }
        }
      };
      const response = await client.search({ body, next_batch: cursor || undefined }, signal);
      const roomEvents = response?.search_categories?.room_events || {};

      return {
        hits: normalizeHits((roomEvents.results || []).map((result) => result.result), { client, tokens }),
        cursor: roomEvents.next_batch || null,
        total: Number.isInteger(roomEvents.count) ? roomEvents.count : null
      };
    }
  };
}

function joinedRoomIds(client) {
  return client.getRooms()
    .filter((room) => room.getMyMembership() === "join")
    .map((room) => room.roomId);
}

// Raw events from the server, newest first, into hits the lists can show.
export function normalizeHits(events, { client, tokens = [] }) {
  const hits = new Map();

  for (const event of events) {
    if (!event || event.type !== "m.room.message" || event.unsigned?.redacted_because) continue;

    const room = client.getRoom(event.room_id);
    if (!room || room.getMyMembership() !== "join") continue;

    // An edit stands for the message it edits; the newest one — the first
    // the server returns — wins.
    const { targetId, content, isEdit } = resolveEditTarget(event);
    if (!content || hits.has(targetId)) continue;

    // The server indexes the whole body, a reply's quoted fallback included. A
    // hit only the quote matched is not one the user can find in the message.
    const text = messageSearchText(content);
    if (tokens.length && !matchesAllPrefixes(text, tokens)) continue;

    hits.set(targetId, {
      eventId: targetId,
      roomId: event.room_id,
      sender: event.sender,
      // An edit is younger than its message: its time must not cut a jump short.
      ts: isEdit ? null : event.origin_server_ts,
      msgtype: content.msgtype,
      text
    });
  }

  return [...hits.values()];
}
